import React from "react";

import clsx from "clsx";
import { capitalize } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useFormulaBuilder } from "@features/custom-metrics/hooks/use-formula-builder";
import { type SalesPageMetric } from "@north-beam/nb-client-side";

interface Props {
  isDisabled?: boolean;
  metricCategories: Record<string, SalesPageMetric[]>;
  selectedCategory: string;
  clearSearch: () => void;
}

export const MetricList = ({
  isDisabled,
  metricCategories,
  selectedCategory,
  clearSearch,
}: Props) => {
  const { addVariableOperand } = useFormulaBuilder();

  const selectMetric = (metric: SalesPageMetric) => {
    addVariableOperand(metric.id);
    clearSearch();
  };

  const renderMetrics = (metrics: SalesPageMetric[]) => {
    if (metrics.length === 0) {
      return (
        <div className="text-sm py-1.5 px-4 text-gray+2">No metrics found</div>
      );
    }

    return (
      <>
        {metrics.map((metric) => (
          <MetricOption
            key={metric.id}
            metric={metric}
            selectMetric={() => selectMetric(metric)}
          />
        ))}
      </>
    );
  };

  const content =
    selectedCategory === "all"
      ? Object.entries(metricCategories).map(([category, metrics]) => (
          <div key={category} className="mb-1">
            <div className="text-xxs text-gray+2 font-medium mb-1 px-4">
              {capitalize(category)}
            </div>
            {renderMetrics(metrics)}
          </div>
        ))
      : renderMetrics(metricCategories[selectedCategory]);

  return (
    <div
      className={clsx(
        { "pointer-events-none opacity-60": isDisabled },
        "overflow-hidden flex-1 flex flex-col",
      )}
    >
      <div className="overflow-auto flex-1">{content}</div>
    </div>
  );
};

const MetricOption = ({
  metric,
  selectMetric,
}: {
  metric: SalesPageMetric;
  selectMetric: () => void;
}) => (
  <div
    className="text-sm py-1.5 px-4 hover:bg-cosmic-3 cursor-pointer"
    onClick={selectMetric}
  >
    <GenericTooltip
      mouseEnterDelay={0.75}
      noInfoCircle
      placement="left"
      showTooltip={!!metric.metadata.tooltip?.description}
      content={
        <div className="w-64">{metric.metadata.tooltip?.description}</div>
      }
    >
      {metric.name}
    </GenericTooltip>
  </div>
);
